/**
 * {@link DataTable} for pairs.
 */
import * as React from 'react';
import * as XLSX from 'xlsx';
import { HeartHandshake } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { PairRow } from './PairRow';
import { PairCard } from './PairCard';
import { PairFiltersContent } from './PairFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderPairDetails } from './renderPairDetails';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { useDataTable } from '@/hooks/useDataTable';
import {
  getSessionStudentTutorPairs,
  getSessionStudentTutorPair,
} from '@/services/inperson/pairs.service';
import { cn } from '@/lib/utils';
import {
  DEFAULT_PAIR_INPERSON_FILTERS as DEFAULT_FILTERS,
  ALL_PAIR_INPERSON_FILTER_GROUP_IDS as ALL_FILTER_GROUP_IDS,
  PAIR_INPERSON_FILTER_LABELS as FILTER_LABELS,
  type PairInPersonFilters as Filters,
  type PairInPersonFilterGroupId as FilterGroupId,
} from '@/components/pairs/pair-configs';
import type { ElementWidth } from '@/types/interface-elements.types';
import {
  buildExportRow,
  buildInPersonPairsListParams,
  getPairExportHeaders,
  getPairDeleteItemName,
  hasActiveFilters as checkActiveFilters,
  toggleListValue,
  type DataTablePageActions,
} from './pair-table-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';

/** Props for {@link PairsDataTable}. */
interface PairsDataTableProps {
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  pageActions?: DataTablePageActions;
}

/** In-person pairs list: session pairs, filters, expand, Excel export. */
export const PairsDataTable: React.FC<PairsDataTableProps> = ({
  layoutWidth: layoutWidthProp = 'full',
  isSection = false,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  pageActions,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;
  const [isExporting, setIsExporting] = React.useState(false);

  const dt = useDataTable<Filters, FilterGroupId>({
    tableName: 'matching-pairs',
    defaultFilters: DEFAULT_FILTERS,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['matching-pairs', dt.currentPage, dt.itemsPerPage, dt.filters],
    queryFn: async () => {
      return await getSessionStudentTutorPairs(
        buildInPersonPairsListParams(dt.filters, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
        }),
      );
    },
  });

  const pairs = React.useMemo(() => apiResponse?.data || [], [apiResponse?.data]);
  const totalItems = apiResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const isEmptyForExport = React.useMemo(() => !pairs || pairs.length === 0, [pairs]);

  const {
    data: expandedPairData,
    isLoading: isLoadingExpandedPair,
    isError: isExpandedPairError,
    error: expandedPairError,
  } = useQuery({
    queryKey: ['matching-pairs', 'single', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      return await getSessionStudentTutorPair(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedPairError,
    error: expandedPairError,
    catalogKey: 'pairNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const expandedItem = expandedPairData ?? null;

  const hasActiveFilters = React.useMemo(() => checkActiveFilters(dt.filters), [dt.filters]);

  const clearFilters = React.useCallback(() => {
    dt.setFilters(DEFAULT_FILTERS);
    dt.setCurrentPage(1);
  }, [dt]);

  const deleteMutation = useMutation({
    mutationFn: (_id: string) => {
      throw new Error(getErrorMessage('pairDeleteNotImplemented'));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['matching-pairs'] });
      dt.setDeleteId(null);
      toast.success('Binôme supprimé avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const pairToDelete = React.useMemo(
    () => pairs.find((p) => p.id === dt.deleteId),
    [pairs, dt.deleteId],
  );

  const handleExportExcel = React.useCallback(() => {
    if (isEmptyForExport) {
      return;
    }

    setIsExporting(true);

    try {
      const rows = pairs.map(buildExportRow);

      const worksheet = XLSX.utils.aoa_to_sheet([getPairExportHeaders(), ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Matchings');
      XLSX.writeFile(workbook, `matchings-${new Date().toISOString().slice(0, 10)}.xlsx`);

      toast.success('Export Excel réussi !');
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [pairs, isEmptyForExport]);

  const toggleTypeFilter = React.useCallback(
    (type: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        types: toggleListValue(prev.types, type),
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setTutorSearch = React.useCallback(
    (search: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, tutorSearch: search }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setStudentSearch = React.useCallback(
    (search: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, studentSearch: search }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDateRange = React.useCallback(
    (range: Filters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        customDateFrom: from,
        customDateTo: to,
        dateRange: from || to ? 'all' : prev.dateRange,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEstablishment = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, establishmentId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSubject = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, subjectId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <PairFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleType={toggleTypeFilter}
        onSetTutorSearch={setTutorSearch}
        onSetStudentSearch={setStudentSearch}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
        onSetEstablishment={setEstablishment}
        onSetSubject={setSubject}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      toggleTypeFilter,
      setTutorSearch,
      setStudentSearch,
      setDateRange,
      setCustomDateRange,
      setEstablishment,
      setSubject,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  return (
    <DataTable
      data={pairs}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="binômes"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={7}
      toolbarActions={
        <>
          <Button
            preset="toolbar-filter"
            className={cn(dt.showFilters && 'section-action-button-active')}
            showBadge={hasActiveFilters}
            onClick={() => {
              const next = !dt.showFilters;
              dt.setShowFilters(next);
              if (!next) dt.setShowConfigure(false);
            }}
          />

          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des binômes en Excel'
              }
            />
          )}
        </>
      }
      filtersPanel={
        <DataTableFilters
          show={dt.showFilters}
          hasActiveFilters={hasActiveFilters}
          filterGroups={filterGroups}
          onClearFilters={clearFilters}
          resultCount={totalItems}
          resultLabel="binôme(s)"
          onToggleConfigure={
            dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
          }
          showConfigure={dt.showConfigure}
          configureContent={
            <div className="flex flex-wrap gap-3">
              {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                const isOn = dt.filterVisibility[id] ?? true;
                return (
                  <Button
                    key={id}
                    variant="custom"
                    size="sm"
                    onClick={() => dt.handleToggleFilterVisibility(id)}
                    disabled={dt.isSavingFilterVisibility}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                      dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                      isOn
                        ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                        : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                    )}
                  >
                    {FILTER_LABELS[id]}
                  </Button>
                );
              })}
            </div>
          }
        />
      }
      renderTableHead={() => (
        <tr>
          <th>Élève</th>
          <th>Tuteur</th>
          <th>Séance</th>
          <th>Établissement</th>
          <th>Matière</th>
          <th>Type</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(pair) => (
        <PairRow
          pair={pair}
          onView={onToggleRow ? () => onToggleRow(pair.id) : () => dt.setExpandedRowId(pair.id)}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(pair) => (
        <DetailsViewContent>{renderPairDetails(pair)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(pair) => (
        <PairCard
          pair={pair}
          onView={onToggleRow ? () => onToggleRow(pair.id) : () => dt.setExpandedRowId(pair.id)}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={HeartHandshake}
      emptyText="Aucun binôme trouvé"
      title="Binômes"
      sidebarMenuId="menu-pairs"
      isSection={isSection}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedPair}
      onCloseDetails={onCloseDetails || (() => dt.setExpandedRowId(null))}
      renderDetailsContent={(pair) => (
        <DetailsViewContent>{renderPairDetails(pair)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={pairToDelete ? getPairDeleteItemName(pairToDelete) : undefined}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
